import { history, useModel } from '@umijs/max';
import { Spin, Flex } from 'antd';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from '@/hooks/useAuth';

const loginPath = '/user/login';
const registerPath = '/user/register';
const publicPaths = [loginPath, registerPath]; // 公开页面，无需登录
const adminOnlyPaths = ['/users', '/platforms', '/roles'];

const AuthGate = ({ children }: { children: ReactNode }) => {
  const { user, isLoading } = useAuth();
  const { initialState, setInitialState } = useModel('@@initialState');
  const [locationState, setLocationState] = useState(() => history.location);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // 菜单和路由权限读取 initialState，必须随当前登录账号一起更新。
    void setInitialState((state) => ({
      ...state,
      currentUser: user ?? undefined,
    }));
  }, [user, isLoading, setInitialState]);

  useEffect(() => {
    const unlisten = history.listen(({ location }) => {
      setLocationState(location);
    });
    return () => {
      unlisten();
    };
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const { pathname, search } = locationState;
    const isStandardUser = user?.globalRole === 'USER';
    const isAdminPath = adminOnlyPaths.some((adminPath) => pathname.startsWith(adminPath));
    const isPublicPath = publicPaths.some((path) => pathname === path);

    // 未登录且访问非公开页面，重定向到登录页
    if (!user && !isPublicPath) {
      const searchParams = new URLSearchParams(search);
      const hasOAuthParams = searchParams.has('client_id') && searchParams.has('redirect_uri');

      let targetSearch = '';
      if (hasOAuthParams) {
        targetSearch = search || '';
      } else {
        const redirectTarget = `${pathname}${search || ''}`;
        const redirectParams = new URLSearchParams();
        redirectParams.set('redirect', redirectTarget);
        targetSearch = `?${redirectParams.toString()}`;
      }

      history.push(`${loginPath}${targetSearch}`);
      return;
    }

    if (user && isStandardUser && isAdminPath) {
      // 普通用户看不到统计页，落点是「我的平台」
      history.replace('/my-platforms');
      return;
    }

    if (user && locationState.pathname === loginPath) {
      // ⚠️ 检查是否是 OAuth 流程：如果 URL 中有 OAuth 参数，不要自动跳转
      const searchParams = new URLSearchParams(locationState.search);
      const hasOAuthParams = searchParams.has('client_id') && searchParams.has('redirect_uri');
      // add=1：已登录的人主动来加一个账号（多账号共存），别把他弹回去。
      const isAddingAccount = searchParams.has('add');

      if (!hasOAuthParams && !isAddingAccount) {
        // 纯登录流程：跳转到 dashboard
        const redirectParam = searchParams.get('redirect');
        const redirectTarget = redirectParam || '/my-platforms';
        const sanitizedTarget = isStandardUser && adminOnlyPaths.some((path) => redirectTarget.startsWith(path))
          ? '/my-platforms'
          : redirectTarget;
        history.push(sanitizedTarget);
      }
      // 否则保持在登录页，让 OAuth 流程自行处理
    }
  }, [user, isLoading, locationState]);

  const isPermissionStateSynced = initialState?.currentUser?.id === user?.id
    && initialState?.currentUser?.globalRole === user?.globalRole;

  if (isLoading || !isPermissionStateSynced) {
    return (
      <Flex align="center" justify="center" style={{ height: '100vh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  // 公开页面不需要等待用户加载
  const isPublicPath = publicPaths.some((path) => locationState.pathname === path);
  if (isPublicPath) {
    return <>{children}</>;
  }

  // 非公开页面等待用户加载完成
  if (!user) {
    return (
      <Flex align="center" justify="center" style={{ height: '100vh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return <>{children}</>;
};

const AuthBoundary = ({ children }: { children: ReactNode }) => (
  <AuthProvider>
    <AuthGate>{children}</AuthGate>
  </AuthProvider>
);

export default AuthBoundary;
